import { Controller, FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { DatePicker } from 'antd';
import { cn } from '../../utils';
import { CustomRegisterOptions } from '../../types';
import dayjs from 'dayjs';

interface DatePickerProps<T extends FieldValues> {
  name: Path<T>;
  control?: UseFormReturn<T>['control'];
  form?: UseFormReturn<T>;
  rules?: CustomRegisterOptions<T>;
  label?: string;
  formState?: UseFormReturn<T>['formState'];
  addOnClass?: string;
  config?: Record<string, any>;
  disabledDate?: (current: dayjs.Dayjs) => boolean;
}

/**
 * A reusable component for rendering a DatePicker field with validation and error handling.
 *
 * @template T - The type of the form values.
 * @param {DatePickerProps<T>} props - The properties for the DatePicker component.
 * @param {Path<T>} props.name - The name of the DatePicker field.
 * @param {UseFormReturn<T>['control']} [props.control] - The control object from react-hook-form.
 * @param {CustomRegisterOptions<T>} [props.rules] - The validation rules for the DatePicker field.
 * @param {string} [props.label] - The label for the DatePicker field.
 * @param {UseFormReturn<T>['formState']} [props.formState] - The form state from react-hook-form.
 * @param {string} addOnClass - Additional Classes for the date input
 * @param {Record<string, any>} [props.config={}] - Additional configuration for the DatePicker field.
 * @returns {JSX.Element} - The rendered DatePicker component.
 */
const DatePickerWrapper = <T extends FieldValues>({
  name,
  control,
  form,
  rules,
  label,
  formState,
  addOnClass = '',
  config = {},
  disabledDate,
}: DatePickerProps<T>) => {
  const effectiveControl = control || form?.control;
  const effectiveFormState = formState || form?.formState;
  const errors = (effectiveFormState?.errors || {}) as Record<string, any>;

  if (!name || !effectiveControl) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-medium text-text-100 whitespace-nowrap">
          {label}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field }) => {
          const { onChange, value, ...rest } = field;

          const handleChange = (date: dayjs.Dayjs | null) => {
            onChange(date ? date.toISOString() : null);
          };

          return (
            <DatePicker
              className={addOnClass}
              {...rest}
              {...config}
              disabledDate={disabledDate}
              value={value ? dayjs(value) : null}
              onChange={handleChange}
              format={{
                format: 'YYYY-MM-DD',
                type: 'mask',
              }}
            />
          );
        }}
      />

      <div
        className={cn(
          'text-xs text-red-500',
          { visible: errors[name] },
          { invisible: !errors[name] },
        )}
      >
        {errors[name] && `${errors[name]?.message}`}
      </div>
    </div>
  );
};


export default DatePickerWrapper;
